export const API_URL = import.meta.env.VITE_API_URL || '/api/v1'
export const APP_NAME = import.meta.env.VITE_APP_NAME || 'Garage Management System'
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0'

// Token storage key
export const TOKEN_KEY = 'garage_access_token'

// API endpoints
export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/auth/login',
  REFRESH: '/auth/refresh',
  LOGOUT: '/auth/logout',
  ME: '/auth/me',
  
  // Users
  USERS: '/users/',
  USERS_ME: '/users/me',
  
  // Roles & RBAC
  ROLES: '/permissions/roles',
  
  // Customers
  CUSTOMERS: '/customers',
  CUSTOMER_DETAILS: (id: number) => `/customers/${id}/details`,
  CUSTOMER_STATS: (id: number) => `/customers/${id}/stats`,
  
  // Vehicles
  VEHICLES: '/vehicles',
  VEHICLE_HISTORY: (id: number) => `/vehicles/${id}/history`,
  VEHICLE_MAINTENANCE: (id: number) => `/vehicles/${id}/maintenance-status`,
  
  // Work Orders
  WORK_ORDERS: '/work-orders',
  WORK_ORDERS_NEXT_NUMERO: '/work-orders/next-numero-scheda',
  WORK_ORDER_STATUS: (id: number) => `/work-orders/${id}/status`,
  WORK_ORDERS_STATS: '/work-orders/stats',
  WORK_ORDERS_CALENDAR: '/work-orders/calendar',

  // Cost items
  COST_ITEMS_OPTIONS: '/cost-items/options',
  WORK_ORDER_COST_ITEMS: (id: number) => `/work-orders/${id}/cost-items`,
  COST_ITEM: (id: number) => `/cost-items/${id}`,

  // Acquisti — interventi fermi in attesa di ricambi
  INTERVENTI_FERMI: '/acquisti/interventi-fermi',
  // Tre segmenti: non intercettabile da /acquisti/interventi-fermi, che ne ha due
  ACQUISTI_INTERVENTO: (id: number) => `/acquisti/intervento/${id}`,

  // Tariffe orarie per reparto e parametri di listino
  TARIFFE: '/tariffe',
  TARIFFA: (id: number) => `/tariffe/${id}`,
  // Tre segmenti: /tariffe/parametri verrebbe intercettato da /tariffe/{id}
  PARAMETRI_LISTINO: '/tariffe/parametri/listino',

  // Payments
  PAYMENTS_OPTIONS: '/payments/options',
  WORK_ORDER_PAYMENTS: (id: number) => `/work-orders/${id}/payments`,
  PAYMENT: (id: number) => `/payments/${id}`,
  PAYMENT_INCASSA: (id: number) => `/payments/${id}/incassa`,
  
  // Parts
  PARTS: '/parts',
  PARTS_INVENTORY: '/parts/inventory',
  PARTS_STOCK: (id: number) => `/parts/${id}/stock`,
  PARTS_CATEGORIES: '/parts/categories/list',
  PARTS_SUPPLIERS: '/parts/suppliers/list',
  
  // Tires
  TIRES: '/tires',
  TIRES_VEHICLE: (vehicleId: number) => `/tires/vehicle/${vehicleId}`,
  TIRES_ROTATION: '/tires/rotation',
  TIRES_ALERTS: '/tires/alerts/replacement-needed',
  TIRES_STATS: '/tires/stats/summary',
  
  // Courtesy Cars
  COURTESY_CARS: '/courtesy-cars',
  COURTESY_CARS_AVAILABLE: '/courtesy-cars/available',
  COURTESY_CAR_LOAN: (id: number) => `/courtesy-cars/${id}/loan`,
  COURTESY_CAR_RETURN: (id: number) => `/courtesy-cars/${id}/return`,
  COURTESY_CAR_MAINTENANCE: (id: number) => `/courtesy-cars/${id}/maintenance`,
  COURTESY_CARS_STATS: '/courtesy-cars/stats/summary',
  
  // Maintenance Schedules
  MAINTENANCE: '/maintenance-schedules',
  MAINTENANCE_SCHEDULES: '/maintenance-schedules',
  MAINTENANCE_ALERTS: '/maintenance-schedules/alerts',
  MAINTENANCE_VEHICLE: (vehicleId: number) => `/maintenance-schedules/vehicle/${vehicleId}`,
  MAINTENANCE_COMPLETE: (id: number) => `/maintenance-schedules/${id}/complete`,
  MAINTENANCE_SKIP: (id: number) => `/maintenance-schedules/${id}/skip`,
  MAINTENANCE_STATS: '/maintenance-schedules/stats/summary',
  
  // Notifications
  NOTIFICATIONS: '/notifications',
  NOTIFICATIONS_BULK: '/notifications/bulk',
  NOTIFICATIONS_MARK_READ: '/notifications/mark-read',
  NOTIFICATIONS_READ: (id: number) => `/notifications/${id}/read`,
  NOTIFICATIONS_UNREAD_COUNT: '/notifications/me/unread-count',
  NOTIFICATIONS_STATS: '/notifications/stats/summary',
  
  // Calendar Events
  CALENDAR_EVENTS: '/calendar-events',
  CALENDAR_EVENTS_VIEW: '/calendar-events/view',
  CALENDAR_EVENT_STATUS: (id: number) => `/calendar-events/${id}/status`,
  CALENDAR_EVENTS_SYNC: '/calendar-events/sync',
  CALENDAR_EVENTS_STATS: '/calendar-events/stats/summary',
  
  // Documents
  DOCUMENTS: '/documents',
  DOCUMENT_UPLOAD: (id: number) => `/documents/${id}/upload`,
  DOCUMENT_DOWNLOAD: (id: number) => `/documents/${id}/download`,
  DOCUMENTS_ENTITY: (entityType: string, entityId: number) => `/documents/entity/${entityType}/${entityId}`,
  DOCUMENTS_STATS: '/documents/stats/summary',
  
  // Activity Logs
  ACTIVITY_LOGS: '/activity-logs',
  ACTIVITY_LOGS_AUDIT: (entityType: string, entityId: number) => `/activity-logs/audit/${entityType}/${entityId}`,
  ACTIVITY_LOGS_USER: (userId: number) => `/activity-logs/user/${userId}/history`,
  ACTIVITY_LOGS_RECENT: '/activity-logs/recent/all',
  ACTIVITY_LOGS_STATS: '/activity-logs/stats/summary',
  ACTIVITY_LOGS_USER_ACTIVITY: '/activity-logs/stats/user-activity',
  
  // Dashboard
  DASHBOARD_SUMMARY: '/dashboard/summary',
  DASHBOARD_ALERTS: '/dashboard/alerts',
  DASHBOARD_RECENT_ACTIVITY: '/dashboard/recent-activity',
  DASHBOARD_STATS: '/dashboard/stats/overview',
  DASHBOARD_STATUS_CARDS: '/dashboard/status-cards',
  
  // System Tables
  SYSTEM_DAMAGE_TYPES: '/system-tables/damage-types',
  SYSTEM_CUSTOMER_TYPES: '/system-tables/customer-types',
  SYSTEM_WORKORDER_STATUS_TYPES: '/system-tables/work-order-status-types',
  SYSTEM_PRIORITY_TYPES: '/system-tables/priority-types',
  SYSTEM_INTERVENTION_STATUS_TYPES: '/system-tables/intervention-status-types',
  SYSTEM_INTERVENTION_RESOLUTION_MODES: '/system-tables/intervention-resolution-modes',
  SYSTEM_INSURANCE_BRANCH_TYPES: '/system-tables/insurance-branch-types',
  SYSTEM_PURCHASE_REQUEST_STATUSES: '/system-tables/purchase-request-statuses',
  // Tutti gli stati, disattivati compresi. La GET qui sopra filtra
  // `attivo == true`: va bene per le tendine, non per il pannello che deve
  // poter riaccendere uno stato spento.
  SYSTEM_PURCHASE_REQUEST_STATUSES_ALL: '/system-tables/purchase-request-statuses-list',
  SYSTEM_WORKSHOP_TYPES: '/system-tables/workshop-types',
  SYSTEM_TABLES: '/system-tables',
  
  // Vista Operativa Officina — statistiche dashboard CMM
  CMM_STATS: '/operativo/stats',
  
  // Intervention endpoints
  CREATE_INTERVENTION: (workOrderId: number) => `/work-orders/${workOrderId}/interventions`,
  INTERVENTION_STATUS: (workOrderId: number, interventionId: number) => 
    `/work-orders/${workOrderId}/interventions/${interventionId}/status`,
  
  // Suppliers (Fornitori)
  SUPPLIERS: '/suppliers',
  SUPPLIER_DETAIL: (id: number) => `/suppliers/${id}`,
  
  // Purchase Requests (RDA - Richieste di Acquisto)
  PURCHASE_REQUESTS: '/purchase-requests',
  PURCHASE_REQUEST_DETAIL: (id: number) => `/purchase-requests/${id}`,
  PURCHASE_REQUEST_SUBMIT: (id: number) => `/purchase-requests/${id}/submit`,
  PURCHASE_REQUEST_APPROVE: (id: number) => `/purchase-requests/${id}/approve`,
  PURCHASE_REQUEST_RECEIVE: (id: number) => `/purchase-requests/${id}/receive`,
  PURCHASE_REQUEST_AUDIT: (id: number) => `/purchase-requests/${id}/audit`,
  
  // Auto (marche, modelli, carburanti)
  AUTO_MARCHE: '/auto/marche',
  AUTO_MODELLI: (marca: string) => `/auto/modelli/${encodeURIComponent(marca)}`,
  AUTO_CARBURANTI: '/auto/carburanti',
  AUTO_VERIFICA_TARGA: '/auto/verifica-targa',
  AUTO_ANALIZZA_LIBRETTO: '/auto/analizza-libretto',
  AUTO_MARCHE_CUSTOM: '/auto/marche-custom',
  AUTO_MARCHE_CUSTOM_DELETE: (id: number) => `/auto/marche-custom/${id}`,
  AUTO_MODELLI_CUSTOM: '/auto/modelli-custom',
  AUTO_MODELLI_CUSTOM_DELETE: (id: number) => `/auto/modelli-custom/${id}`,
  AUTO_SEED_MARCHE: '/auto/seed-marche-default',
  AUTO_SYNC_CARQUERY: '/auto/sync-carquery',

  // Tire Service (cambio pneumatici stagionale)
  TIRE_SERVICE: {
    SETS: '/tire-service/sets',
    SET_BY_ID: (id: number) => `/tire-service/sets/${id}`,
    SET_STATO: (id: number) => `/tire-service/sets/${id}/stato`,
    SET_QR_LABEL: (id: number) => `/tire-service/sets/${id}/qr-label`,
    VEHICLE_SETS: (vehicleId: number) => `/tire-service/vehicles/${vehicleId}/sets`,
    PRENOTAZIONI: '/tire-service/prenotazioni',
    PRENOTAZIONE_BY_ID: (id: number) => `/tire-service/prenotazioni/${id}`,
    PRENOTAZIONE_QR_IMAGE: (id: number) => `/tire-service/prenotazioni/${id}/qr-image`,
    PRENOTAZIONE_STATO: (id: number) => `/tire-service/prenotazioni/${id}/stato`,
    PRENOTAZIONI_GIORNO: (data: string) => `/tire-service/prenotazioni/giorno/${data}`,
    QR_SCAN: (codice: string) => `/tire-service/qr/scan/${codice}`,
    ORDINI_LAVORO: '/tire-service/ordini-lavoro',
    ORDINE_LAVORO_BY_ID: (id: number) => `/tire-service/ordini-lavoro/${id}`,
    ORDINE_CHIUDI: (id: number) => `/tire-service/ordini-lavoro/${id}/chiudi`,
    ORDINE_TESTO_NOTIFICA: (id: number) => `/tire-service/ordini-lavoro/${id}/testo-notifica-pronto`,
    REMINDER_GENERA: (id: number) => `/tire-service/reminder/genera/${id}`,
    REMINDER_INVIA: (id: number) => `/tire-service/reminder/invia/${id}`,
    REMINDER_BULK: '/tire-service/reminder/bulk',
    DASHBOARD: '/tire-service/dashboard',
    REPORT_OCCUPAZIONE: '/tire-service/report/occupazione',
  },

  SPEECH: {
    DG_TOKEN: '/speech/dg-token',
  },

  // Settings (configurazioni e integrazioni esterne)
  SETTINGS: '/settings',
  SETTINGS_PUBLIC: '/settings/public',
  SETTING_BY_KEY: (chiave: string) => `/settings/${chiave}`,

  // Auth — reset password e attivazione
  VALIDATE_TOKEN: '/auth/validate-token',
  SET_PASSWORD: '/auth/set-password',
  FORGOT_PASSWORD: '/auth/forgot-password',
}
